import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './components/ErrorBoundary';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Training from './pages/Training';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Unsubscribe from './pages/Unsubscribe';
import Products from './pages/Products';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';
import './styles/responsive.css';
import { HelmetProvider } from 'react-helmet-async';
import { ReactLenis } from 'lenis/react';

const WebDevelopment = lazy(() => import('./pages/Services/WebDevelopment'));
const MobileDevelopment = lazy(() => import('./pages/Services/MobileDevelopment'));
const CloudServices = lazy(() => import('./pages/Services/CloudServices'));
const Cybersecurity = lazy(() => import('./pages/Services/Cybersecurity'));
const DataIntelligence = lazy(() => import('./pages/Services/DataIntelligence'));
const DigitalAcceleration = lazy(() => import('./pages/Services/DigitalAcceleration'));
const ObservabilityMonitoring = lazy(() => import('./pages/Services/ObservabilityMonitoring'));
const AnswerRankDetail = lazy(() => import('./pages/Products/AnswerRankDetail'));
const HireSmartDetail = lazy(() => import('./pages/Products/HireSmartDetail'));
const DataNexisDetail = lazy(() => import('./pages/Products/DataNexisDetail'));

const AppContent = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <div className="App">
      <ScrollToTop />
      <Header />
      <main className={`main-content ${!isHomePage ? 'non-home' : ''}`}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            // Reset the state of your app here
            window.location.reload();
          }}
          onError={(error, info) => {
            // Log the error to your error reporting service
            console.error('Error caught by boundary:', error, info);
          }}
        >
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/products" element={<Products />} />
              <Route path="/services" element={<Services />} />
              <Route path="/training" element={<Training />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/services/web-development" element={<WebDevelopment />} />
              <Route path="/services/mobile" element={<MobileDevelopment />} />
              <Route path="/services/cloud" element={<CloudServices />} />
              <Route path="/services/security" element={<Cybersecurity />} />
              <Route path="/services/data" element={<DataIntelligence />} />
              <Route path="/services/consulting" element={<DigitalAcceleration />} />
              <Route path="/services/observability" element={<ObservabilityMonitoring />} />
              <Route path="/products/firmasearch" element={<AnswerRankDetail />} />
              <Route path="/products/hiresmart" element={<HireSmartDetail />} />
              <Route path="/products/orchix-ai" element={<DataNexisDetail />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
};

const App = () => {
  return (
    <ReactLenis root options={{ lerp: 0.1, duration: 1.2, smoothTouch: true }}>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </HelmetProvider>
    </ReactLenis>
  );
};

export default App;
